import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { GiftToggle, GiftFields } from "@/components/books/GiftFields";
import { BOOK_CTA } from "@/lib/books";

const inputCls = "w-full rounded-lg bg-white px-4 py-3 font-body text-sm outline-none border focus:border-[var(--turquoise)] transition-colors";
const inputStyle = { borderColor: "rgba(11,27,61,0.18)", color: "var(--navy)" };
const FIELDS = [
  { name: "parent_name", label: "Parent's Name", giftLabel: "Your Name" },
  { name: "email", label: "Email", giftLabel: "Your Email", type: "email" },
  { name: "phone", label: "Phone Number", giftLabel: "Your Phone", type: "tel" },
  { name: "child_first_name", label: "Child's First Name" },
];

const ThankYou = () => (
  <div className="text-center py-14 px-6" data-testid="book-inquiry-success">
    <Heart size={34} className="mx-auto mb-5" style={{ color: "var(--gold)" }} />
    <p className="font-display text-2xl md:text-3xl leading-snug" style={{ color: "var(--navy)" }}>
      Thank you! Coach Mimi will personally reach out to help create your little swimmer's special book.
    </p>
  </div>
);

export const BookInquiryForm = () => {
  const [form, setForm] = useState(() => ({ consent: false, is_gift: new URLSearchParams(window.location.search).get("gift") === "1" }));
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const isGift = !!form.is_gift;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/book-inquiries", form);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please call 561-577-6346.");
    } finally {
      setSending(false);
    }
  };

  if (done) return <ThankYou />;

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="book-inquiry-form">
      <GiftToggle isGift={isGift} onChange={(v) => set("is_gift", v)} />
      {FIELDS.map((f) => (
        <label key={f.name} className="block">
          <span className="overline block mb-2" style={{ color: "var(--navy)" }}>{isGift && f.giftLabel ? f.giftLabel : f.label} *</span>
          <input type={f.type || "text"} required className={inputCls} style={inputStyle} data-testid={`book-field-${f.name}`} onChange={(e) => set(f.name, e.target.value)} />
        </label>
      ))}
      {isGift && <GiftFields inputCls={inputCls} inputStyle={inputStyle} set={set} />}
      <label className="block sm:col-span-2">
        <span className="overline block mb-2" style={{ color: "var(--navy)" }}>Dedication (optional)</span>
        <textarea rows={2} maxLength={300} placeholder={isGift ? "e.g. To our brave little fish — love, Grandma & Grandpa" : "e.g. To our brave little swimmer — we're so proud of you!"} className={inputCls} style={inputStyle} data-testid="book-field-dedication" onChange={(e) => set("dedication", e.target.value)} />
      </label>
      <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer" data-testid="book-field-consent">
        <input type="checkbox" required checked={!!form.consent} className="w-5 h-5 mt-0.5 shrink-0 accent-[var(--navy)]" data-testid="book-field-consent-checkbox" onChange={(e) => set("consent", e.target.checked)} />
        <span className="font-body text-sm leading-relaxed" style={{ color: "var(--muted)" }}>
          {isGift
            ? "I understand Coach Mimi will contact the child's parent or legal guardian privately, and that the child's photos will only be used after the parent gives written authorization. Photos and book pages will never be published."
            : "I am this child's parent or legal guardian. I understand Coach Mimi will request reference photos privately, and that my child's photos will only be used to create our book after I give my written authorization. Photos and book pages will never be published."}
        </span>
      </label>
      <button type="submit" disabled={sending} className="btn-primary sm:col-span-2 justify-center" data-testid="book-inquiry-submit">
        {sending ? <Loader2 size={18} className="animate-spin" /> : BOOK_CTA}
      </button>
    </form>
  );
};
